import { useState, useEffect, useCallback } from 'react';
import { db, ActivityLogRecord, ActivityType } from '../lib/database';
import { 
  Activity, 
  ChevronDown, 
  ChevronUp, 
  Clock, 
  Download, 
  Filter, 
  Palette, 
  Plus, 
  Tag, 
  Trash2, 
  Upload,
  User,
  XCircle,
  type LucideIcon,
} from 'lucide-react';

const ACTIVITY_TYPE_CONFIG: Record<ActivityType, { icon: LucideIcon; label: string; color: string }> = {
  palette_created: { icon: Plus, label: 'Created', color: 'text-green-600' },
  palette_updated: { icon: Activity, label: 'Updated', color: 'text-blue-600' },
  palette_deleted: { icon: Trash2, label: 'Deleted', color: 'text-red-600' },
  palette_imported: { icon: Upload, label: 'Imported', color: 'text-purple-600' },
  palette_exported: { icon: Download, label: 'Exported', color: 'text-indigo-600' },
  palette_audited: { icon: Activity, label: 'Audited', color: 'text-orange-600' },
  palette_compared: { icon: Activity, label: 'Compared', color: 'text-cyan-600' },
  colors_added: { icon: Plus, label: 'Colors Added', color: 'text-green-600' },
  colors_removed: { icon: XCircle, label: 'Colors Removed', color: 'text-red-600' },
  tags_modified: { icon: Tag, label: 'Tags Modified', color: 'text-yellow-600' },
};

interface ActivityFeedPanelProps {
  paletteId?: string;
  limit?: number;
}

export function ActivityFeedPanel({ paletteId, limit = 50 }: ActivityFeedPanelProps) {
  const [activities, setActivities] = useState<ActivityLogRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<ActivityType | 'all'>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const loadActivities = useCallback(async () => {
    setLoading(true);
    try {
      let result: ActivityLogRecord[];
      
      if (paletteId) {
        result = await db.getActivityLogByPalette(paletteId, limit);
      } else if (filterType !== 'all') {
        result = await db.getActivityLogByType(filterType, limit);
      } else {
        result = await db.getActivityLog(limit);
      }
      
      setActivities(result);
    } catch (error) {
      console.error('Failed to load activities:', error);
    } finally {
      setLoading(false);
    }
  }, [paletteId, filterType, limit]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const formatFullTimestamp = (timestamp: number): string => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const handleClearAll = async () => {
    if (confirm('Are you sure you want to clear all activity logs? This cannot be undone.')) {
      await db.clearActivityLog();
      loadActivities();
    }
  };

  const activityTypes = Object.keys(ACTIVITY_TYPE_CONFIG) as ActivityType[];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            {paletteId ? 'Palette Activity' : 'Recent Activity'}
          </h3>
          <span className="text-sm text-gray-500">({activities.length})</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Filter className="w-4 h-4" />
            Filter
            {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {activities.length > 0 && !paletteId && (
            <button
              onClick={handleClearAll}
              className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
            >
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Activity Type
          </label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as ActivityType | 'all')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Activities</option>
            {activityTypes.map((type) => (
              <option key={type} value={type}>
                {ACTIVITY_TYPE_CONFIG[type].label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Activity List */}
      {activities.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No activities yet</p>
          <p className="text-sm mt-1">Activity will appear here as you work with palettes</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {activities.map((activity) => {
            const config = ACTIVITY_TYPE_CONFIG[activity.type];
            const Icon = config.icon;
            const isExpanded = expandedItems.has(activity.id!);
            const hasDetails = activity.details && Object.keys(activity.details).length > 0;

            return (
              <div
                key={activity.id}
                className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className={`flex-shrink-0 ${config.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {activity.description}
                        </p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {activity.author}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span title={formatFullTimestamp(activity.timestamp)}>
                              {formatTimestamp(activity.timestamp)}
                            </span>
                          </span>
                          {activity.paletteName && !paletteId && (
                            <span className="flex items-center gap-1">
                              <Palette className="w-3 h-3" />
                              {activity.paletteName}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Expand button */}
                      {hasDetails && (
                        <button
                          onClick={() => toggleExpanded(activity.id!)}
                          className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 rounded"
                        >
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && hasDetails && (
                      <div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200 text-xs space-y-2">
                        {activity.details?.colorCount !== undefined && (
                          <div>
                            <span className="font-medium text-gray-700">Color Count:</span>{' '}
                            <span className="text-gray-600">{activity.details.colorCount}</span>
                          </div>
                        )}
                        {activity.details?.addedColors && activity.details.addedColors.length > 0 && (
                          <div>
                            <span className="font-medium text-gray-700">Added Colors:</span>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {activity.details.addedColors.map((color: string, idx: number) => (
                                <div
                                  key={idx}
                                  className="flex items-center gap-1 px-2 py-1 bg-white rounded border border-gray-200"
                                >
                                  <div
                                    className="w-4 h-4 rounded border border-gray-300"
                                    style={{ backgroundColor: color }}
                                  />
                                  <span className="text-gray-700 font-mono">{color}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {activity.details?.removedColors && activity.details.removedColors.length > 0 && (
                          <div>
                            <span className="font-medium text-gray-700">Removed Colors:</span>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {activity.details.removedColors.map((color: string, idx: number) => (
                                <div
                                  key={idx}
                                  className="flex items-center gap-1 px-2 py-1 bg-white rounded border border-gray-200"
                                >
                                  <div
                                    className="w-4 h-4 rounded border border-gray-300"
                                    style={{ backgroundColor: color }}
                                  />
                                  <span className="text-gray-700 font-mono">{color}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {activity.details?.addedTags && activity.details.addedTags.length > 0 && (
                          <div>
                            <span className="font-medium text-gray-700">Added Tags:</span>{' '}
                            <span className="text-gray-600">
                              {activity.details.addedTags.join(', ')}
                            </span>
                          </div>
                        )}
                        {activity.details?.removedTags && activity.details.removedTags.length > 0 && (
                          <div>
                            <span className="font-medium text-gray-700">Removed Tags:</span>{' '}
                            <span className="text-gray-600">
                              {activity.details.removedTags.join(', ')}
                            </span>
                          </div>
                        )}
                        {activity.details?.exportFormat && (
                          <div>
                            <span className="font-medium text-gray-700">Export Format:</span>{' '}
                            <span className="text-gray-600">{activity.details.exportFormat}</span>
                          </div>
                        )}
                        {activity.details?.importFormat && (
                          <div>
                            <span className="font-medium text-gray-700">Import Format:</span>{' '}
                            <span className="text-gray-600">{activity.details.importFormat}</span>
                          </div>
                        )}
                        {activity.details?.auditPassRate !== undefined && (
                          <div>
                            <span className="font-medium text-gray-700">Audit Pass Rate:</span>{' '}
                            <span className="text-gray-600">
                              {(activity.details.auditPassRate * 100).toFixed(1)}%
                            </span>
                          </div>
                        )}
                        {activity.details?.comparedWith && (
                          <div>
                            <span className="font-medium text-gray-700">Compared With:</span>{' '}
                            <span className="text-gray-600">{activity.details.comparedWith}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
